import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { Textarea } from "@material-tailwind/react";


/* Components */
import TechnicianSelect from "../technician/TechnicianSelect";
import ContactSelect from "../contact/ContactSelect";
import CustomerSelect from "../customer/CustomerSelect";
import CustomerInfoCard from "../customer/CustomerInfoCard";
import ContactInfoCard from "../contact/ContactInfoCard";
import TechnicianInfoCard from "../technician/TechnicianInfoCard";
import AddressSelect from "../address/AddressSelect";
import { getProspectDateTimeLocalValue } from "../../../services/prospect/prospect";


/* Services */
import { validator } from "../../../services/form-validator/form-validator";

/* Validator */
export const ProspectFormValidator = {
  customer: (e: unknown) => validator.isNotNull(e),
  contact: (e: unknown) => validator.isNotNull(e),
  technician: (e: unknown) => validator.isNotNull(e),
  date: (e: unknown) => validator.isString(e) || validator.isNumber(e)
};

const v = ProspectFormValidator; // shorthand
/***/

/**
* ProspectForm - Prospect edit/create form
* @param data - prospect data for edition or empty object for creation
* @param onChange - field change event
* @param onSubmit - form submit event
* @param submitted - is form submitted, check missing field
* @param isEditing - is form in editing mode
*/
export default function ProspectForm({
  data,
  onChange,
  onSubmit,
  submitted,
  isEditing
}: {
  data?: EasyTech.Prospect,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean,
  isEditing?: boolean
}) {
  const currentData = useMemo(() => data || {} as EasyTech.Prospect, [data]);
  const formIdentity = useMemo(
    () => currentData.id || `create:${currentData.customer?.id || "empty"}`,
    [currentData.id, currentData.customer?.id]
  );
  const [locationAddress, setLocationAddress] = useState<EasyTech.Address | undefined>(undefined);
  // Ne désactiver le champ client que lors de l'édition, pas lors de la création
  const initialCustomer = !!(isEditing && data !== undefined && data.customer !== null);

  /**
  * Hydrate the address input only when a new visit form is opened.
  * This avoids resetting the label on every parent re-render and prevents
  * unwanted reverse geocoding after local autocomplete selections.
  */
  useEffect(() => {
    if (currentData.address) {
      setLocationAddress(currentData.address);
      return;
    }

    if (currentData.location?.latitude === undefined || currentData.location?.longitude === undefined) {
      setLocationAddress(undefined);
      return;
    }

    const isCustomerAddress =
      currentData.customer?.address?.latitude === currentData.location.latitude &&
      currentData.customer?.address?.longitude === currentData.location.longitude;

    if (isCustomerAddress && currentData.customer?.address) {
      setLocationAddress({
        ...currentData.customer.address,
        displayLabel: [
          currentData.customer.address.address,
          currentData.customer.address.addressCompl,
          [currentData.customer.address.zipCode, currentData.customer.address.city].filter(Boolean).join(" ")
        ].filter(Boolean).join(", ")
      });
      return;
    }
  }, [formIdentity]);

  /**
  * handleChange: handle field change event
  * @param name - field name
  * @param value - param value 
  */
  const handleChange = (name: string, value: any) => {
    if (name === "customer") {
      const latitude = value?.address?.latitude;
      const longitude = value?.address?.longitude;

      if (!locationAddress && latitude !== undefined && longitude !== undefined) {
        onChange("location", undefined);
      }
    }

    onChange(name, value);
  };
  /***/

  const handleLocationAddressChange = (value?: EasyTech.Address) => {
    setLocationAddress(value);
    onChange("address", value);

    if (!value) {
      handleChange("location", undefined);
      return;
    }

    const isCustomerAddress =
      currentData.customer?.address?.latitude === value.latitude &&
      currentData.customer?.address?.longitude === value.longitude;

    if (isCustomerAddress) {
      handleChange("location", undefined);
      return;
    }

    handleChange("location", {
      latitude: value.latitude,
      longitude: value.longitude,
      type: "LOCATION",
    });
  };

  /**
  * Customer shortcut button behavior:
  * 1. keep the visit payload on CUSTOMER fallback by clearing custom location
  * 2. immediately reuse the customer address label in the input
  * 3. never call reverse geocoding here
  */
  const handleCustomerAddressClick = () => {
    const customerAddress = currentData.customer?.address;
    if (!customerAddress?.latitude || !customerAddress?.longitude) return;

    handleChange("location", undefined);
    onChange("address", customerAddress);
    setLocationAddress({
      ...customerAddress,
      displayLabel: [
        customerAddress.address,
        customerAddress.addressCompl,
        [customerAddress.zipCode, customerAddress.city].filter(Boolean).join(" ")
      ].filter(Boolean).join(", ")
    });
  };

  const handleDateTimeChange = (value: string) => {
    if (!value) {
      handleChange("date", "");
      handleChange("hour", "");
      return;
    }

    const [datePart, timePart = "00:00"] = value.split("T");
    const [year, month, day] = datePart.split("-").map(Number);

    if ([year, month, day].some((part) => Number.isNaN(part))) {
      handleChange("date", "");
      handleChange("hour", "");
      return;
    }

    const normalizedDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
    handleChange("date", normalizedDate);
    handleChange("hour", timePart.slice(0, 5));
  };

  const handleDateTimeClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;

    try {
      if ("showPicker" in target) {
        target.showPicker();
      }
    } catch (err) {
      // Ignore browsers that block or do not support programmatic picker opening.
    }
  };

  return (
    <>
      <div className="field">
        <label>{t("common:customer")} *</label>
        <CustomerSelect className={`${(submitted && !validator.isFieldValid(currentData, "customer", v)) && "error"}`}
          onChange={(value) => handleChange("customer", value)}
          value={currentData.customer}
          disabled={initialCustomer} />
      </div>

      {currentData.customer &&
        <CustomerInfoCard customer={currentData.customer} />
      }

      {currentData?.customer &&
        <div className="field">
          <label>{t("common:contact")} *</label>
          <ContactSelect className={`${(submitted && !validator.isFieldValid(currentData, "contact", v)) && "error"}`}
            onChange={(value) => handleChange("contact", value)}
            value={currentData.contact}
            customerId={currentData.customer.id} />
        </div>
      }

      {currentData.contact &&
        <ContactInfoCard contact={currentData.contact} />
      }

      <div className="field">
        <label>{t("common:technician")} *</label>
        <TechnicianSelect className={`${(submitted && !validator.isFieldValid(currentData, "technician", v)) && "error"}`}
          onChange={(value) => handleChange("technician", value)}
          value={currentData.technician} />
      </div>

      {currentData.technician &&
        <TechnicianInfoCard technician={currentData.technician} />
      }

      <div className="field">
        <label>{t("common:date:label")} *</label>
        <input
          type="datetime-local"
          className={`form-field ${(submitted && !validator.isFieldValid(currentData, "date", v)) ? "error" : ""}`}
          value={getProspectDateTimeLocalValue(currentData)}
          onChange={(e) => handleDateTimeChange(e.target.value)}
          onClick={handleDateTimeClick}
          step={60}
        />
      </div>

      <AddressSelect
        value={locationAddress}
        label="common:address"
        onChange={handleLocationAddressChange}
        customerAddress={currentData.customer?.address}
        allowEditCustomerAddress={true}
        onCustomerAddressClick={handleCustomerAddressClick}
        className="form-field"
      />

      <div className="field">
        <label>{t("form:prospect:remarks")}</label>
        <Textarea className="form-field"
          placeholder="ex: ..."
          value={currentData.remarks}
          onChange={(value) => handleChange("remarks", value.target.value)}>
        </Textarea>
      </div>
    </>
  );
}
